import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { Plus, Pencil, Trash2, X, Save } from "lucide-react"
import { PageHeader } from "@/components/portal/PageHeader"
import { Loading, ErrorBox } from "@/components/portal/LoadingError"
import { toast } from "@/components/portal/Toast"
import { cn } from "@/lib/utils"

type Kunde = { slug: string; name: string; tenants_count: number }

async function fetchKunden(): Promise<{ items: Kunde[]; total: number }> {
  const r = await fetch("/api/v1/kunden", { credentials: "include" })
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

export default function KundenList() {
  const qc = useQueryClient()
  const { data, isLoading, error, isFetching, refetch } = useQuery({
    queryKey: ["kunden"],
    queryFn: fetchKunden,
  })

  const [editing, setEditing] = useState<Kunde | null>(null)
  const [creating, setCreating] = useState(false)

  const del = useMutation({
    mutationFn: async (slug: string) => {
      const r = await fetch(`/api/v1/kunden/${slug}`, { method: "DELETE", credentials: "include" })
      if (!r.ok && r.status !== 204) {
        const d = await r.json().catch(() => ({}))
        throw new Error(d.detail || `HTTP ${r.status}`)
      }
    },
    onSuccess: () => {
      toast("✓ Kunde gelöscht")
      qc.invalidateQueries({ queryKey: ["kunden"] })
    },
    onError: (e) => toast("Löschen fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error"),
  })

  if (error) return <div className="pp-main"><ErrorBox error={error} /></div>

  const items = data?.items || []

  return (
    <>
      <PageHeader
        title="Kunden"
        count={data?.total}
        onRefresh={() => refetch()}
        refreshing={isFetching}
      >
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="pp-btn pp-btn-sm pp-btn-primary"
        >
          <Plus className="w-3.5 h-3.5" /> Neuer Kunde
        </button>
      </PageHeader>

      <p style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 16 }}>
        Kunden sind reine Gruppierungscontainer. HERO-Token und IMAP-Zugangsdaten werden pro Tenant konfiguriert.
      </p>

      {isLoading ? (
        <Loading label="Lädt Kunden..." />
      ) : items.length === 0 ? (
        <div className="pp-card" style={{ padding: 48, textAlign: "center", color: "var(--text-muted)" }}>
          Noch kein Kunde angelegt
        </div>
      ) : (
        <div className="pp-card" style={{ padding: 0 }}>
          <div className="pp-tbl-wrap" style={{ border: "none", boxShadow: "none", margin: 0 }}>
            <table>
              <thead>
                <tr>
                  <th>Slug</th>
                  <th>Name</th>
                  <th style={{ width: 120 }}>Tenants</th>
                  <th style={{ width: 140 }}>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {items.map((k) => (
                  <tr key={k.slug}>
                    <td style={{ fontFamily: "monospace", fontSize: 12, color: "var(--text-muted)" }}>
                      <Link to={`/kunde/${k.slug}`} style={{ color: "var(--accent-color)", textDecoration: "none" }}>
                        {k.slug}
                      </Link>
                    </td>
                    <td style={{ fontWeight: 500 }}>{k.name}</td>
                    <td style={{ color: "var(--text-muted)" }}>
                      {k.tenants_count} {k.tenants_count === 1 ? "Tenant" : "Tenants"}
                    </td>
                    <td>
                      <div style={{ display: "inline-flex", gap: 4 }}>
                        <button
                          type="button"
                          className="task-btn"
                          onClick={() => setEditing(k)}
                          title="Bearbeiten"
                        >
                          <Pencil className="w-3.5 h-3.5" /> Bearbeiten
                        </button>
                        <button
                          type="button"
                          className="task-btn task-btn-icon task-btn-danger"
                          onClick={() => {
                            if (confirm(`Kunde "${k.name}" löschen? Tenants bleiben erhalten.`)) {
                              del.mutate(k.slug)
                            }
                          }}
                          disabled={del.isPending}
                          title="Löschen"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <KundeEditModal
        open={editing !== null}
        onClose={() => setEditing(null)}
        kunde={editing}
      />
      <KundeEditModal
        open={creating}
        onClose={() => setCreating(false)}
        kunde={null}
      />
    </>
  )
}

function KundeEditModal({
  open, onClose, kunde,
}: {
  open: boolean
  onClose: () => void
  kunde: Kunde | null
}) {
  const qc = useQueryClient()
  const isCreate = !kunde
  const [slug, setSlug] = useState("")
  const [name, setName] = useState("")
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setSlug(kunde?.slug || "")
      setName(kunde?.name || "")
      setErr(null)
    }
  }, [open, kunde])

  const save = useMutation({
    mutationFn: async () => {
      if (!name.trim()) throw new Error("Name fehlt")
      if (isCreate) {
        if (!slug.trim()) throw new Error("Slug fehlt")
        const r = await fetch("/api/v1/kunden", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ slug, name }),
        })
        const d = await r.json().catch(() => ({}))
        if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`)
      } else {
        const r = await fetch(`/api/v1/kunden/${kunde!.slug}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ name }),
        })
        const d = await r.json().catch(() => ({}))
        if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`)
      }
    },
    onSuccess: () => {
      toast(isCreate ? "✓ Kunde angelegt" : "✓ Kunde gespeichert")
      qc.invalidateQueries({ queryKey: ["kunden"] })
      onClose()
    },
    onError: (e) => setErr(e instanceof Error ? e.message : String(e)),
  })

  if (!open) return null

  return (
    <div
      className="pp-overlay"
      data-portal-modal
      style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.5)",
               display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <form
        onSubmit={(ev) => { ev.preventDefault(); setErr(null); save.mutate() }}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-card)", borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,.25)",
          width: "100%", maxWidth: 480,
        }}
      >
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h2 className="text-[15px] font-semibold">
            {isCreate ? "Neuer Kunde" : "Kunde bearbeiten"}
          </h2>
          <button type="button" onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          {err && <div className="pp-alert pp-alert-err">{err}</div>}

          <div>
            <label className="pp-form-label">
              Slug {!isCreate && <span className="text-[10px] text-muted-foreground font-normal">(unveränderlich)</span>}
            </label>
            <input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              disabled={!isCreate}
              required={isCreate}
              placeholder="z.B. kanaltec"
              style={!isCreate ? { background: "var(--secondary-bg)", color: "var(--text-muted)" } : undefined}
            />
            {isCreate && (
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 3 }}>
                Lowercase + Underscores, keine Leerzeichen
              </div>
            )}
          </div>

          <div>
            <label className="pp-form-label">Firmenname *</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="z.B. Mustermann GmbH"
            />
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button type="button" onClick={onClose} className="pp-btn pp-btn-sm">Abbrechen</button>
          <button
            type="submit"
            disabled={!name.trim() || (isCreate && !slug.trim()) || save.isPending}
            className={cn(
              "pp-btn pp-btn-primary pp-btn-sm",
              (!name.trim() || (isCreate && !slug.trim()) || save.isPending) && "opacity-50 cursor-not-allowed",
            )}
          >
            <Save className="w-3 h-3" /> {save.isPending ? "Speichert..." : "Speichern"}
          </button>
        </div>
      </form>
    </div>
  )
}
